import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@groot/shell/lib/api";
import type { ShipmentTracking, TrackingEvent } from "./useTracking";

export interface OrderItem {
  id: number;
  orderId: number;
  productName: string;
  size: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  printFormat?: "mini" | "medium" | "large" | null;
}

export interface Order {
  id: number;
  orderNumber: string;
  publicLink: string;
  customerId: number;
  orderDate: string;
  status: "new" | "printing" | "shipped" | "delivered" | "cancelled";
  total: number;
  subtotal: number | null;
  discountType: "flat" | "percentage" | null;
  discountValue: number | null;
  discountAmount: number | null;
  discountReason: string | null;
  paymentStatus: "not_paid" | "paid";
  paymentDate: string | null;
  paymentMethod: string | null;
  expectedDeliveryDate: string | null;
  shippingProvider: string | null;
  trackingId: string | null;
  shippingCost: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  customer?: {
    id: number;
    name: string;
    mobileNumber: string | null;
    address?: string | null;
    email?: string | null;
    instagramHandle?: string | null;
  };
  items?: OrderItem[];
  _count?: {
    items: number;
  };
  photoRequests?: Array<{
    id: number;
    status: string;
    publicLink: string;
    _count: {
      files: number;
    };
  }>;
  /** Public order page only: shared print previews ("sneak peek" links). */
  printPreviews?: Array<{
    previewLink: string;
    sharedAt: string;
    submittedAt: string | null;
  }>;
  /** Public order page only: stored Speed Post tracking (no live fetch). */
  tracking?: { tracking: ShipmentTracking; events: TrackingEvent[] } | null;
}

export interface CreateOrderDTO {
  customerId: number;
  orderDate?: string;
  status?: "new" | "printing" | "shipped" | "delivered" | "cancelled";
  discountType?: "flat" | "percentage";
  discountValue?: number;
  discountReason?: string;
  paymentStatus?: "not_paid" | "paid";
  paymentDate?: string;
  paymentMethod?: string;
  expectedDeliveryDate?: string;
  shippingCost?: number;
  shippingProvider?: string;
  trackingId?: string;
  notes?: string;
  items: Array<{
    productName: string;
    size: string;
    quantity: number;
    unitPrice: number;
  }>;
}

export interface UpdateOrderDTO {
  id: number;
  data: Partial<Omit<CreateOrderDTO, "items">>;
}

export interface UpdateOrderItemsDTO {
  id: number;
  items: Array<{
    productName: string;
    size: string;
    quantity: number;
    unitPrice: number;
  }>;
}

export function useOrders() {
  return useQuery({
    queryKey: ["orders"],
    queryFn: async () => {
      return apiClient.get<Order[]>("/orders");
    },
  });
}

export function useOrder(id: number) {
  return useQuery({
    queryKey: ["orders", id],
    queryFn: async () => {
      return apiClient.get<Order>(`/orders/${id}`);
    },
    enabled: !!id,
  });
}

export function useCreateOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateOrderDTO) => {
      return apiClient.post<Order>("/orders", input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}

export function useUpdateOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: UpdateOrderDTO) => {
      return apiClient.put<Order>(`/orders/${id}`, data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}

export function useUpdateOrderItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, items }: UpdateOrderItemsDTO) => {
      return apiClient.put<Order>(`/orders/${id}/items`, { items });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", variables.id] });
    },
  });
}

export function useDeleteOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/orders/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });
}

export function useOrderByPublicLink(publicLink: string) {
  return useQuery({
    queryKey: ["orders", "public", publicLink],
    queryFn: async () => {
      return apiClient.get<Order>(`/public/orders/${publicLink}`);
    },
    enabled: !!publicLink,
  });
}

export interface UpdateCustomerInfoDTO {
  address: string;
  mobileNumber: string;
  email?: string;
  instagramHandle?: string;
}

export function useUpdateCustomerInfo(publicLink: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateCustomerInfoDTO) => {
      return apiClient.patch<unknown>(`/public/orders/${publicLink}/customer-info`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders", "public", publicLink] });
    },
  });
}
